import {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { FormSpy, useForm, useFormState } from 'react-final-form';
import { differenceInDays, startOfDay } from 'date-fns';
import { useCurrencyContext } from 'src/common/providers/CurrencyProvider';
import { usePreviewModeContext } from 'src/common/providers/PreviewModeProvider';
import { useUserContext } from 'src/common/providers/UserProvider';
import { formatDate } from 'src/common/utils/dateHelpers';
import {
  getDurationHours,
  getEstimatedRate,
  getPriceDetails,
  priceIsDay,
} from 'src/listing/helper';
import { DepartureTime, TripCartAttentionField, TripCartFormState } from 'src/listing/types';
import useBookingMotivatorsContext, { BlockedTime } from 'src/common/providers/useBookingMotivators';
import { useTripCartContext } from 'src/trip-cart/hooks/useTripCartContext';
import getTripCartOptions, { TripCartOptions } from 'src/trip-cart/hooks/useTripCartOptionsContext/getTripCartOptions';
import calculatePrices from 'src/trip-cart/utils/calculatePrices';
import getBlockedTimesForDate from 'src/trip-cart/utils/getBlockedTimesForDate';
import {
  getCachedBlockedTimes, getCachedTierPrices, setCachedBlockedTimes, setCachedTierPrices,
} from 'src/trip-cart/utils/tripCartCache';
import {
  getDateRangeForDuration,
} from 'src/trip-cart/utils/tripCartUtils';
import { trackTripCartPrePopulated, trackTripCartPriceCalculated } from 'src/trip-cart/utils/trackingTripCart';
import { RecentInquiryData } from 'src/common/utils/recentInquiryCookieData';
import useDebouncedCallback from 'src/common/hooks/useDebouncedCallback';
import useFeatureFlag from 'src/common/hooks/useFeatureFlag';
import { useTranslation } from 'next-i18next';
import { TripCartPrice } from 'src/common/types';
import { DurationUnit } from 'src/common/types/api/listings';
import { FULL_DAY_HOURS } from 'src/listing/constants';
import { useListingMotivators } from 'src/listing/hooks/useListingPageMotivators';
import broadcastNotification from 'src/common/components/Notification/broadcastNotification';
import { useTripCartBookingContext } from 'src/trip-cart/hooks/useTripCartBookingContext';
import useAfterRenderCallback from 'src/common/hooks/useAfterRenderCallback';
import { useTripCartSeasonsContext } from '../useTripCartSeasonsContext';

type Sequence = keyof Pick<
TripCartFormState,
| 'isMultiDay'
| 'formSelectedDates'
| 'formDuration'
| 'formGuests'
| 'withCaptain'
| 'departureTime'
>
| 'step.start'
| 'step.formSelectedDatesPost'
| 'step.pricing'
| 'step.formSelectedDatesForSeason'
| 'step.setupSeasonDepartureTimes'
| 'step.handlePostSeasonSetup'
| null;

const TRACKING_DEBOUNCE_MS = 700;

export type TripCartOptionsContextProps = {
  options: TripCartOptions;

  // The form options are being calculated
  optionsLoading: boolean;

  // `optionsLoading === true` or prices are being retrieved
  priceLoading: boolean;
  prices: TripCartPrice;
  // Only for current date
  blockedTimes: BlockedTime[];
  blockedTimesLoading: boolean;
  /** TODO: comment relevant?
   * departure times for selected instabook trip or undefined if no instabook */
  departureTimes: DepartureTime[] | undefined;
  getFormattedEstimatedRate: (rate: string, isHeader?: boolean) => string;
  priceUnit: DurationUnit;
  addAttentionField: (field: keyof TripCartFormState) => void;
  removeAttentionField: (field: keyof TripCartFormState) => void;
  getPricePlaceHolder: (isHeader?: boolean) => string;
  attentionFields: (keyof TripCartFormState)[];
};

type TripCartFormOptionsProviderProps = {
  tripCartCookieData?: Partial<RecentInquiryData>;
  children: ReactNode;
};

const TripCartOptionsContext = createContext<TripCartOptionsContextProps>(
  {} as TripCartOptionsContextProps,
);

const TripCartOptionsProvider: FC<TripCartFormOptionsProviderProps> = ({
  tripCartCookieData,
  children,
}) => {
  const [t] = useTranslation('listing-page');
  const {
    listing,
    status,
  } = useTripCartContext();
  const {
    values, errors: formErrors, submitFailed,
  } = useFormState<TripCartFormState>();
  const form = useForm<TripCartFormState>();
  const { currency } = useCurrencyContext();
  const [prices, setPrices] = useState<TripCartPrice>({ price_sections: [] } as TripCartPrice);
  const [blockedTimes, setBlockedTimes] = useState<BlockedTime[]>([]);
  const [blockedTimesLoading, setBlockedTimesLoading] = useState(false);
  const { setListingBlockedTimes } = useListingMotivators();
  const { getSelectedMotivators, isDateUnavailable } = useBookingMotivatorsContext();
  const {
    setupSeasonForDate,
    setupDepartureTimesForDuration,
    setupDepartureTimesForMultiDayPricing,
    durationsForDate,
    multiDaysDurationsForDate,
    departureTimesForDate,
  } = useTripCartSeasonsContext();

  // Use for form changes that need special loading or sequencing
  const [sequence, setSequenceInternal] = useState<Sequence>(null);
  const [previousSequence, setPreviousSequence] = useState<Sequence>(null);
  const [hasInit, setHasInit] = useState(false);
  const [hasTrackedPrePopulation, setHasTrackedPrePopulation] = useState(false);

  const isSummaryEnabled = useFeatureFlag('unified-trip-cart--summary');
  const isInstabookEnabled = useFeatureFlag('pricing-v2--instabook');
  const unitPriceDetail = useMemo(() => getPriceDetails(listing.prices) ?? {}, [listing.prices]);
  const { user } = useUserContext();
  const { previewMode } = usePreviewModeContext();

  const [priceUnit, setPriceUnit] = useState<DurationUnit>('hour');
  const [attentionFields, setAttentionFields] = useState<(keyof TripCartFormState)[]>([]);
  // Which errors have been handled by attention
  const [attentionErrors, setAttentionErrors] = useState<Set<keyof TripCartFormState>>(new Set());
  const { setTierPricing } = useTripCartBookingContext();

  const options: TripCartOptionsContextProps['options'] = useMemo(
    () => getTripCartOptions({
      user,
      listing,
      values,
      unitPriceDetail,
      isDateUnavailable,
      departureTimes: departureTimesForDate,
      blockedTimes,
      previewMode,
      hasSubmitted: submitFailed,
      t,
      featureFlags: {
        summary: isSummaryEnabled,
        instabook: isInstabookEnabled,
      },
    }),
    [
      user,
      values,
      listing,
      unitPriceDetail,
      isDateUnavailable,
      departureTimesForDate,
      blockedTimes,
      previewMode,
      submitFailed,
      isSummaryEnabled,
      isInstabookEnabled,
      t,
    ],
  );

  const setSequence = useCallback((value: Sequence) => {
    setPreviousSequence(sequence);
    setSequenceInternal(value);
  }, [sequence]);

  const trackPrepopulated = useCallback(() => {
    trackTripCartPrePopulated({
      originalDates: tripCartCookieData?.dates,
      originalCapacity: Number(tripCartCookieData?.capacity) || 0,
      originalDuration: tripCartCookieData?.duration,
      mappedDates: values.formSelectedDates.map((d) => formatDate(d)),
      mappedCapacity: values.formGuests,
      mappedDuration: values.formDuration,
    });
  }, [tripCartCookieData, values]);

  const showNetworkError = useCallback(() => {
    broadcastNotification({
      i18nKey: 'trip-cart--network-loss',
      translation: 'listing-page',
      variant: 'error',
    });
  }, []);

  const trackPricingShown = useDebouncedCallback(() => {
    if (!hasTrackedPrePopulation) {
      trackPrepopulated();
      setHasTrackedPrePopulation(true);
    }

    trackTripCartPriceCalculated({
      isBookNowVisible: false,
      isBookNowEnabled: false,
      isMultiDay: values.isMultiDay,
      costType: options.isCostConfirmed ? 'confirmed-cost' : 'estimated-cost',
      capacity: values.formGuests,
      duration: values.formDuration,
    });
  }, TRACKING_DEBOUNCE_MS);

  const showTierPricing = useCallback(async () => {
    const { formDuration } = values;
    const params = {
      captainOption: `${values.withCaptain}`,
      guests: values.formGuests ? `${values.formGuests}` : '',
      duration: values.formDuration,
      currency: currency.code,
      date: formatDate(values.formSelectedDates[0]),
    };

    // Hard caching. Only cleared on page reload
    const cache = getCachedTierPrices(params);
    if (cache) {
      setPrices(cache);
      trackPricingShown();
      setTierPricing({
        params,
        prices: cache,
      });
      return;
    }
    setTierPricing(undefined);
    const priceData = await calculatePrices(listing.id, { ...params });
    if (!priceData || !priceData.price_sections?.length) {
      showNetworkError();
      return;
    }

    const {
      renter_booking_amount: renterAmount,
      renter_currency: renterCurrency,
    } = priceData;
    const rate = getEstimatedRate(renterAmount ?? 0, formDuration, renterCurrency ?? currency);
    priceData.rate = rate;

    setCachedTierPrices(params, priceData);
    setPrices(priceData);
    setTierPricing({
      params,
      prices: priceData,
    });

    trackPricingShown();
  }, [values, currency, listing.id, trackPricingShown, showNetworkError, setTierPricing]);

  const removeAttentionField = useCallback((field: keyof TripCartFormState) => {
    const previous = new Set(attentionFields);
    previous.delete(field);
    setAttentionFields(Array.from(previous));
  }, [attentionFields]);

  const addAttentionField = useCallback((field: TripCartAttentionField) => {
    // show change to user
    const previous = new Set(attentionFields);
    previous.add(field);
    setAttentionFields(Array.from(previous));
  }, [attentionFields]);

  const updateBlockedTimesForSelectedDate = useCallback(async () => {
    // Hard caching. Only cleared on page reload
    const params = {
      date: formatDate(values.formSelectedDates[0]),
    };
    const cache = getCachedBlockedTimes(params);
    if (cache) {
      setBlockedTimes(cache);
      setBlockedTimesLoading(false);
      return;
    }

    setBlockedTimesLoading(true);
    const times = await getBlockedTimesForDate(listing.id, values.formSelectedDates[0]);

    setBlockedTimes(times);
    setCachedBlockedTimes(params, times);
    setListingBlockedTimes(times);
    setBlockedTimesLoading(false);
  }, [listing.id, values.formSelectedDates, setListingBlockedTimes]);

  /**
   * IMPORTANT: Ensure that any steps with await setSequence directly after and return.
   * Do not act on existing data after an await as it might be outdated.
   * Do not set state after awaiting except for `setSequence`
   *
   * Special cases:
   * It is ok to use the awaited value to decide the next step, but do not use any other
   * state as part of the check
   * e.g. don't use the awaited value with `values.formDuration`.
   */
  const runSequencing = useAfterRenderCallback(async () => {
    // Do not process if withCaptain is invalid. Allow other errors
    if (options.errors?.withCaptain || previewMode) {
      // if an error occurs it will run through this again, causing
      // double previousSequence to equal sequence, and get stuck
      // so assign null to ensure it won't match
      setPreviousSequence(null);
      return;
    }

    // cases are in order of the dependency chain, e.g. `isMultiDay` will move onto
    // `formSelectedDates`, but it is ok to move back up the chain as needed or skip steps
    switch (sequence) {
      /** Run from the top */
      case 'step.start':
        // fallthrough
      case 'isMultiDay': {
        /**
         * Potential dependency changes: date and duration
         */
        const startDate = values.formSelectedDates[0];

        if (values.isMultiDay) {
          const durationOption = multiDaysDurationsForDate[0];
          // Can occur in previewMode
          if (!durationOption) {
            setSequence('step.formSelectedDatesForSeason');
            return;
          }
          form.change('formDuration', durationOption.durationFormatted);
          const dateRange = getDateRangeForDuration(
            startDate,
            durationOption.duration,
            durationOption.unit,
          );
          if (dateRange[1] !== values.formSelectedDates[1]) {
            addAttentionField('formSelectedDates');
          }
          form.change('formSelectedDates', dateRange);
        } else {
          if (values.formSelectedDates.length > 1) {
            addAttentionField('formSelectedDates');
          }
          form.change('formSelectedDates', [startDate]);
        }

        setSequence('formSelectedDates');
        return;
      }

      /**
       * Potential dependency change: duration
       */
      case 'formSelectedDates': {
        // TODO: should this be a range?
        const motivators = getSelectedMotivators([values.formSelectedDates[0]]);
        const isUnavailable = motivators.includes('UNAVAILABLE')
        || motivators.includes('OFF_SEASON')
        || motivators.includes('NO_SAME_DAY_PRICING');

        if (
          !values.isMultiDay
          && !values.formDuration
          && motivators.includes('NO_SAME_DAY_PRICING')) {
          form.change('isMultiDay', true);
          form.change('isFormStateValid', true);
          setSequence('isMultiDay');
          return;
        }

        if (isUnavailable) {
          form.change('isFormStateValid', false);
          setSequence(null);
          return;
        }

        form.change('isFormStateValid', true);
        const isLimitedAvailability = motivators.includes('LIMITED_AVAILABILITY');
        if (isLimitedAvailability) {
          await updateBlockedTimesForSelectedDate();
        } else {
          setBlockedTimes([]);
        }

        // select date for season
        setSequence('step.formSelectedDatesForSeason');
        return;
      }
      case 'step.formSelectedDatesForSeason': {
        // select date for season
        const success = await setupSeasonForDate(values.formSelectedDates[0], blockedTimes);
        if (!success) {
          setSequence(null);
          return;
        }
        setSequence('step.handlePostSeasonSetup');
        return;
      }
      case 'step.handlePostSeasonSetup': {
        // handle no same day pricing with multiday
        if (values.isMultiDay && !values.formDuration) {
          form.change('formDuration', multiDaysDurationsForDate[0].durationFormatted);
        }

        if (!values.isFormStateValid && multiDaysDurationsForDate.length) {
          const durationOption = multiDaysDurationsForDate[0];
          const startDate = values.formSelectedDates[0];
          const dateRange = getDateRangeForDuration(
            startDate,
            durationOption.duration,
            durationOption.unit,
          );
          form.change('formSelectedDates', dateRange);
          setSequence(null);
          return;
        }

        /*
        Departure time has not initialized. Do this here instead of `step.setupSeasonDepartureTimes`
        as that is only run when durations are changed.
        The value is in the cookie, but cannot be used until season information is retrieved, and
        so it must be set client-side
        */
        if (!values.departureTime && departureTimesForDate && tripCartCookieData?.departureTime) {
          const matchingValue = departureTimesForDate.find((time) => (
            tripCartCookieData.departureTime === time.formattedTime24
          ));
          if (matchingValue) {
            form.change('departureTime', matchingValue.formattedTime24);
          }
        }
        setSequence('step.pricing');
        return;
      }
      case 'formGuests': {
        setSequence('step.pricing');
        return;
      }
      case 'formDuration': {
        const previousOption = durationsForDate.find(
          (option) => option.durationFormatted === values.formDuration,
        );

        if (!previousOption && !values.isMultiDay) {
          // Get closest option in range (previous value out of range so only first and
          // last hours need comparison
          let durationOption = durationsForDate[0];
          if (!values.isMultiDay) {
            const first = durationsForDate[0];
            const last = durationsForDate.findLast((duration) => duration.unit === 'hour')
              ?? first;
            const previous = getDurationHours(values.formDuration);
            durationOption = (
              Math.abs(first.duration - previous) < Math.abs(last.duration - previous)
            )
              ? first
              : last;
          }

          // TODO: change? Can happen for multiday only with instabooks
          if (!durationOption) {
            form.change('isMultiDay', !values.isMultiDay);
            setSequence('isMultiDay');
            return;
          }
          if (values.formDuration !== durationOption.durationFormatted) {
            addAttentionField('formDuration');
          }
          form.change('formDuration', durationOption.durationFormatted);
        } else if (values.isMultiDay) {
          // Need to check if the date range has been updated
          const numDays = values.formSelectedDates.length < 2
            ? NaN
            : Math.abs(differenceInDays(
              startOfDay(values.formSelectedDates[0]),
              startOfDay(values.formSelectedDates[1]),
              // add one since it's inclusive of the final day
            )) + 1;
          // duration is in days since we're already in multiday (unchanged this sequence iteration)
          const durationOption = multiDaysDurationsForDate.find((option) => (
            option.durationFormatted === values.formDuration));

          // Should never happen, backtrack to resolve options again
          if (!durationOption) {
            setSequence('step.start');
            return;
          }

          // Date range has not been updated (either still same day, or different range)
          if (Number.isNaN(numDays) || numDays !== durationOption.duration) {
            const dateRange = getDateRangeForDuration(
              values.formSelectedDates[0],
              durationOption.duration,
              durationOption.unit,
            );
            form.change('formSelectedDates', dateRange);
            // don't trigger 'formSelectedDates' sequence. This is a "soft" update, no further
            // calculations are needed
          }
        }
        setSequence('step.setupSeasonDepartureTimes');
        return;
      }
      case 'step.setupSeasonDepartureTimes': {
        if (values.isMultiDay) {
          setupDepartureTimesForMultiDayPricing();
        } else {
          setupDepartureTimesForDuration({
            duration: getDurationHours(values.formDuration),
            blockedTimes,
          });
        }

        setSequence('step.pricing');
        return;
      }

      case 'withCaptain': {
        // fallthrough
      }

      case 'departureTime': {
        // fallthrough
      }
      case 'step.pricing': {
        // fallthrough
      }
      default: {
        if (!values.isFormStateValid) {
          setSequence(null);
          return;
        }
        const durationHours = getDurationHours(values.formDuration);
        if (durationHours <= FULL_DAY_HOURS) {
          setPriceUnit('hour');
        } else if (priceIsDay(unitPriceDetail.multiday ?? [])) {
          setPriceUnit('day');
        } else {
          setPriceUnit('night');
        }

        // Ensure open-ended departure times
        await showTierPricing();
        setSequence(null);
      }
    }
  });

  const getFormattedEstimatedRate = useCallback((rate: string, isHeader: boolean = false) => {
    if (!rate) return '';

    const isDay = listing.calendar_seasons?.multiday_pricing_unit === 'day';
    let tier = 'hour';
    if (values.isMultiDay) {
      tier = isDay ? 'day' : 'night';
    }
    const estimatedRateFormatted = isHeader
      ? t(`trip-cart--estimated-rate--header--${tier}`, { rate })
      : t(`trip-cart--estimated-rate--${tier}`, { rate });
    return estimatedRateFormatted;
  }, [
    t,
    values.isMultiDay,
    listing.calendar_seasons?.multiday_pricing_unit,
  ]);

  const getPricePlaceHolder = useCallback((isHeader: boolean = false) => {
    const isDay = listing.calendar_seasons?.multiday_pricing_unit === 'day';
    let tier = 'hour';
    if (values.isMultiDay) {
      tier = isDay ? 'day' : 'night';
    }
    const placeholder = currency.code === 'USD' ? `${currency.symbol}...` : `${currency.code}  ${currency.symbol}...`;
    return isHeader
      ? t(`trip-cart--estimated-rate--header--${tier}`, { rate: placeholder })
      : t(`trip-cart--estimated-rate--${tier}`, { rate: placeholder });
  }, [
    listing.calendar_seasons?.multiday_pricing_unit,
    t,
    values.isMultiDay,
    currency.code,
    currency.symbol]);

  const onChange = useCallback(({
    values: {
      isMultiDay, formSelectedDates, formDuration, formGuests, withCaptain,
    },
  }: { values: TripCartFormState }) => {
    if (status !== 'ready') return;

    let pending: Sequence = null;
    if (values.isMultiDay !== isMultiDay) {
      pending = 'isMultiDay';
    } else if (values.formSelectedDates !== formSelectedDates) {
      pending = 'formSelectedDates';
    } else if (values.formDuration !== formDuration) {
      pending = 'formDuration';
    } else if (values.formGuests !== formGuests) {
      pending = 'formGuests';
    } else if (values.withCaptain !== withCaptain) {
      pending = 'withCaptain';
    }

    if (!pending) {
      return;
    }
    // Ensure the current sequencing flow is not disrupted
    if (!sequence) {
      // Nothing to disrupt, run directly
      setSequence(pending);
    }
    // Do nothing if already processing. This onChange will be triggered
    // during runSequence. Disambiguating between that system triggering a change
    // and a user triggering a change is complex, and not necessary
  }, [values, sequence, status, setSequence]);
  // Get initial prices
  useEffect(() => {
    if (status !== 'ready' || hasInit) return;
    setHasInit(true);

    // Start from the top
    setSequence('step.start');
  }, [hasInit, status, setSequence]);

  // Run sequence on change
  useEffect(() => {
    if (status !== 'ready') {
      return;
    }
    if (Object.keys(options.errors ?? {}).length) {
      if (options.errors?.withCaptain && !attentionErrors.has('withCaptain')) {
        // Set to valid option
        form.change('withCaptain', options.withCaptain[0]);
        const fields = new Set(attentionErrors);
        fields.add('withCaptain');
        setAttentionErrors(fields);
        addAttentionField('withCaptain');
      }
      if (options.errors?.departureTime && !attentionErrors.has('departureTime')) {
        // Reset field, don't force a value. This field can be invalid
        // form.change('departureTime', undefined);
        const fields = new Set(attentionErrors);
        fields.add('departureTime');
        setAttentionErrors(fields);
        addAttentionField('departureTime');
      }
    } else if (attentionErrors.keys.length > 0) {
      setAttentionErrors(new Set());
    }

    // useEffect will trigger with any dep change, and `runSequencing` will frequently change due
    // to it's long list of deps. This can cause this to re-run with the same `sequence`.
    // Multiple calls to `runSequencing` will cause issues.
    if (sequence && previousSequence !== sequence) {
      setPreviousSequence(sequence);
      runSequencing();
    }
  }, [
    attentionErrors,
    formErrors,
    addAttentionField,
    form,
    options.errors,
    options.withCaptain,
    previousSequence,
    runSequencing,
    sequence,
    status,
  ]);

  // Clear out on unmount
  useEffect(() => () => {
    setAttentionFields([]);
  }, []);

  const value = useMemo((): TripCartOptionsContextProps => {
    const optionsLoading = values.isFormStateValid && (sequence != null || status !== 'ready');
    const priceLoading = values.isFormStateValid
    && (optionsLoading || !prices?.price_sections?.length);

    return ({
      options,
      optionsLoading,
      priceLoading,
      prices: prices ?? { price_sections: [] },
      blockedTimes,
      blockedTimesLoading,
      getFormattedEstimatedRate,
      priceUnit,
      departureTimes: departureTimesForDate,
      attentionFields,
      getPricePlaceHolder,
      addAttentionField,
      removeAttentionField,
    });
  }, [
    blockedTimes,
    blockedTimesLoading,
    options,
    prices,
    sequence,
    status,
    getFormattedEstimatedRate,
    priceUnit,
    departureTimesForDate,
    attentionFields,
    values.isFormStateValid,
    addAttentionField,
    removeAttentionField,
    getPricePlaceHolder,
  ]);

  return (
    <TripCartOptionsContext.Provider value={value}>
      <FormSpy
        onChange={onChange}
      />
      {children}
    </TripCartOptionsContext.Provider>
  );
};

export default TripCartOptionsProvider;

export const useTripCartOptionsContext = (): TripCartOptionsContextProps => {
  const context = useContext(TripCartOptionsContext);
  if (context == null) {
    throw new Error(
      'useTripCartOptionsContext must be used within a TripCartOptionsContext',
    );
  }
  return context;
};
